import React from 'react';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ReactComponent as Structure } from '../../assets/images/structure.svg';
import { ReactComponent as RuleBased } from '../../assets/images/rule-based.svg';
import { ReactComponent as Adaptive } from '../../assets/images/adaptive.svg';
import { FeatureCard } from './Pricing';
import { YellowBoxBottom } from './Home';

const structured_knowledge = [
  {
    title: 'Structured Rule Embedding',
    description:
      'Core principles from ancient Vedic texts like BPHS and Sutras are not used as static references. Transformed them into structured decision paths within our AI',
    icon: Structure,
  },
  {
    title: 'Rule-Based Layering',
    description:
      'Our AI mimics how real astrologers think using rule engines, knowledge graphs, and time-sequenced logic to analyze planetary positions and more.',
    icon: RuleBased,
  },
  {
    title: 'Adaptive Logic Modules',
    description:
      'As the model encounters more queries, it improves its contextual mapping to different life while remaining rooted in classical calculations.',
    icon: Adaptive,
  },
];

const features = [
  // 'Data privacy',
  'Knowledge Source',
  'Prompt handling',
  'BTR Support',
  'Data Security',
];

const details = [
  // 'Self-hosted AI with complete local data control',
  'Structured Vedic + proprietary analysis',
  'Multi-rule logic stack with Vedic Principle',
  'Fully automated, AI driven BTR engine',
  'Encrypted internal architecture',
];

const infoCards = [
  {
    title: 'Building True Domain-Aligned AI',
    description: `Instead of layering prompts on top of generic large language models (LLMs), we’ve built a system grounded in the actual logic of Vedic astrology using real astrological rules, not pre-written scripts. This makes our responses contextually deep, technically valid, and astrologically sound.`,
  },
  {
    title: 'Prioritizing Data Control and AI Ethics',
    description: `We self-host our models to protect sensitive user information like birth date and time. Your data never leaves our secure environment, and we never monetize it. Our AI is built to serve users not extract from them.`,
  },
  {
    title: 'Delivering Real Logic, Not Just Paragraphs',
    description: `While most platforms generate long, superficial texts based on probability, we simulate how real astrologers think: analyzing planetary sequences, dashas, & house combinations. Our goal is measurable accuracy not vague storytelling.`,
  },
];

const Card = ({ title, description, sx, dotRight }) => {
  const sxValForDot = dotRight ? { left: -7 } : { right: -6 };
  const sxValForLine = dotRight ? { left: -2 } : { right: 0 };
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 3,
        maxWidth: 360,
        width: '100%',
        borderColor: '#e0e0e0',
        backgroundColor: 'transparent',
        borderRadius: 0,
        ...sx,
        // Add right border dot-line with circle
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '1px',
          background: '#909090',
          ...sxValForLine,
        },

        // Add circular dot at mid-point
        '&::before': {
          content: '""',
          position: 'absolute',
          right: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: '#909090',
          ...sxValForDot,
        },
      }}
    >
      <Typography variant="h3" mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
};

const StaggeredInfoCards = () => {
  return (
    <Box
      sx={{
        maxWidth: 900,
        mx: 'auto',
        mt: 6,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 4,
        position: 'relative',
      }}
    >
      {/* Left top card */}
      <Stack direction={'column'} width="719px" alignItems="cneter">
        <Card {...infoCards[0]} sx={{ position: 'relative', left: 0 }} />
        <Card
          {...infoCards[1]}
          sx={{ position: 'relative', right: 0, alignSelf: 'flex-end' }}
          dotRight
        />
        <Card {...infoCards[2]} sx={{ position: 'relative', left: 0 }} />
      </Stack>
    </Box>
  );
};

const FeatureComparison = () => {
  return (
    <Box width="100%" maxWidth={900} mx="auto" py={6}>
      <Stack
        direction="row"
        spacing={4}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {/* Left Column */}
        <Box minWidth="40%">
          <Typography fontWeight="bold" mb={2}>
            Features
          </Typography>
          <Stack spacing={2}>
            {features.map((item, idx) => (
              <Typography key={idx} variant="homePageBody" color="text.primary">
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>

        {/* Right Column */}
        <Box flex={1}>
          <Typography fontWeight="bold" mb={2}>
            What We do
          </Typography>
          <Stack spacing={2}>
            {details.map((item, idx) => (
              <Typography key={idx} variant="homePageBody" color="text.primary">
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const Technology = () => {
  const model = 'Llama';
  return (
    <Box>
      {/* Title */}
      <Typography variant="h1" textAlign="center" gutterBottom>
        Technology at the core of{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>{' '}
        <br />{' '}
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Our System is using {model} which is an enterprise-grade LLM that we
        host on-premises, not via a public API. In practice this means our
        entire infrastructure is maintained and hosted by us, much like leading
        enterprises that self‑host their AI systems. By deploying {model}
        on-prem, we gain full control over the model’s inference. This
        self-contained architecture scales with our needs (we can add compute
        nodes or GPUs as needed) and ensures transparency as well as data
        privacy of our users.
        <br />
        We know exactly what data the model is trained on and how it processes
        each query, rather than treating AI as a “black box”.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        A high‑performance on-premises server cluster (LLM inference rack).
        Hosting {model} locally lets{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> encrypt
        and isolate all data in our own network, rather than sending anything to
        third-party clouds
      </Typography>
      <Divider />
      <Typography variant="h2">
        Native BTR (Birth Time Rectification) Engine
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Most astrology apps take the birth time at face value we don’t. Our AI
        system is equipped with an intelligent Birth Time Rectification (BTR)
        engine that automatically adjusts inaccuracies when it detects
        inconsistencies between the user’s provided time and validated
        astrological patterns.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In other words, we own the disk and memory no third party (not OpenAI,
        Google, or anyone else) can intercept or ingest our inputs. Industry
        analyses stress that self-hosting “ensures [sensitive] data doesn’t
        leave your infrastructure”.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In practical terms, every API call is a local function call: inference
        happens over HTTPS, and all stored data remains behind our firewalls.
        This meets the strictest compliance regimes (GDPR, HIPAA, etc.) because
        data never crosses organisational boundaries.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: 'calc(80px - 36px)' }}>
        Independent Knowledge Processing
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Our model learns and updates only from the domain data we provide. It
        does not send queries or summaries to any external AI. We continuously
        fine-tune and refresh the model using proprietary Vedic astrology
        corpora (texts, charts, historical analysis) and user feedback – all
        inside our environment. This complete autonomy means{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> can
        leverage advanced ML techniques (e.g. reinforcement learning, custom
        reward functions) without leaking any insight. As venture analysts note,
        companies with sensitive data “can completely control how data is used
        and where it’s sent” when they self-host
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In practice, our engineers pipeline new observations (like correction of
        chart calculations or Dasha rules) directly into {model}. The model’s
        “knowledge” remains siloed: it adapts to new patterns we encode but
        never contaminates or depends on an outside service (unless we choose
        to). In short, our AI reasons about Vedic logic in-house – from
        Nakshatra alignments to planetary dashas – using only the transparent
        algorithms and data we own.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: 'calc(80px - 36px)' }}>
        Competitive Edge Protection
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Because we own the full pipeline, competitors cannot passively siphon
        off our intellectual capital. Public LLM services often retain and
        re-incorporate user inputs into future model training.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        OpenAI’s terms explicitly allow user-submitted content to be used for
        improving their models. That means any proprietary astrological
        algorithms or domain-specific insights could, in theory, end up in a
        broadly distributed model. By contrast, with {model} on-premise, none of
        our proprietary data or astrology knowledge is ever exposed externally.
        We effectively “lock” our domain expertise in our private model.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Industry commentators observe that this level of control keeping data
        under lock-and-key is a strategic advantage. Our methods and datasets
        cannot be reverse-engineered or inadvertently shared, protecting{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>’s trade
        secrets (Vedic calculations, chart libraries, etc.) from becoming public
        knowledge. In summary, owning our entire AI stack not only safeguards
        user privacy but also preserves our unique competitive value by
        preventing any leakage of our specialised wisdom.
      </Typography>
      <Divider />
      <Typography variant="h2">Structured Knowledge Injection</Typography>
      <Typography variant="homePageBody" paragraph>
        We are not just prompting an AI to “act like an astrologer.” We are
        building true astrological reasoning into the system:
      </Typography>
      <Stack spacing={6} pt={6}>
        {structured_knowledge.map((item, index) => (
          <Stack alignItems="center" key={index} position="relative" px={1}>
            <FeatureCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              Icon={item.icon}
            />
          </Stack>
        ))}
      </Stack>
      <Divider />
      <Typography variant="homePageBody" paragraph>
        This advanced process
        <List dense disablePadding disableGutters sx={{ mt: 2, mb: 4 }}>
          {[
            'Works silently in the background',
            'Adjusts birth time by a few minutes (and soon, by hours, as our engine evolves)',
            'Relies on planetary patterns, past life event markers, and dashā-based validation models',
            'Is currently offered completely free during our beta phase',
          ].map((point, index) => (
            <ListItem key={index} disableGutters sx={{ pl: 0, py: 0 }}>
              <ListItemText
                slotProps={{ primary: { variant: 'body2' } }}
                primary={`• ${point}`}
              />
            </ListItem>
          ))}
        </List>
      </Typography>
      <Typography variant="homePageBody" paragraph>
        <i>
          We are still in the early stage of this system and in the coming
          months, BTR will become more powerful, capable of cross-referencing
          user-reported life events to fine-tune time far beyond basic
          approximations. It’s a game-changer in predictive accuracy, and we’re
          just getting started.
        </i>
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        Technical Benefits of Our Approach
        <FeatureComparison />
      </Typography>
      <Typography variant="h2">Why It Matters</Typography>
      <Typography variant="homePageBody" paragraph>
        In a domain dominated by generic AI chat layers and inconsistent
        answers, <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>{' '}
        stands apart by:
        <StaggeredInfoCards />
      </Typography>

      <YellowBoxBottom>
        <Typography variant="h2" my={2}>
          Built on Science. Inspired by Soul.
        </Typography>
        <Typography variant="h4" fontWeight={500}>
          <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> is a
          living system constantly learning, adapting, and growing. Our tech is
          a tribute to precision and purity, ensuring every insight we offer is
          meaningful and aligned.
        </Typography>
      </YellowBoxBottom>
    </Box>
  );
};

export default Technology;
